import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { LOADER_BARS, LoaderSize } from './loader-markup';

export type LoaderMode = 'block' | 'inline' | 'overlay' | 'fullscreen';

/**
 * Indicateur de chargement unique de l'application. Le motif et ses variantes vivent dans
 * `src/styles/_loader.scss` (global, partagé avec l'overlay des grilles ag-Grid).
 *
 * - `block` (défaut) : centré dans sa zone, pour un bloc ou un onglet qui charge.
 * - `inline` : dans le flux d'un texte, message à droite.
 * - `overlay` : voile sur la zone — le parent DOIT être en `position: relative`. Le contenu
 *   déjà affiché reste lisible dessous. Z-index et fond : `--moon-loader-z`,
 *   `--moon-loader-overlay-bg`, `--moon-loader-radius` posés sur `<app-loader>`.
 * - `fullscreen` : démarrage de l'app, login.
 *
 * Apparaît après `delay` ms : un chargement plus rapide ne fait rien clignoter. Le délai est
 * porté par le CSS, aucun timer.
 *
 * Pour un bouton en cours d'envoi, ne pas poser ce composant dans le bouton : utiliser
 * `button[appLoading]` (`LoadingButton`), qui conserve le libellé et donc la largeur.
 */
@Component({
  selector: 'app-loader',
  imports: [TranslateModule],
  template: `
    <div
      class="moon-loader-block moon-loader-block--{{ mode() }}"
      [style.--moon-loader-delay]="delay() + 'ms'"
      role="status"
      aria-live="polite"
    >
      <span class="moon-loader moon-loader--{{ size() }}" aria-hidden="true">
        @for (bar of bars; track bar) {
          <span class="moon-loader__bar"></span>
        }
      </span>
      @if (message(); as key) {
        <p class="moon-loader__message">{{ key | translate }}</p>
      } @else {
        <span class="visually-hidden">{{ 'LOADING' | translate }}</span>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      :host(.moon-loader-host--inline) {
        display: inline-block;
      }
    `,
  ],
  host: { '[class.moon-loader-host--inline]': "mode() === 'inline'" },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Loader {
  readonly size = input<LoaderSize>('md');
  readonly mode = input<LoaderMode>('block');
  /** Clé i18n. Sans message, seul un libellé « Chargement » est lu aux lecteurs d'écran. */
  readonly message = input<string>();
  readonly delay = input(150);

  protected readonly bars = LOADER_BARS;
}
